import { useCallback, useState } from 'react'

import CheckIcon from '@/assets/icons/icon-check.svg'
import CloseIcon from '@/assets/icons/icon-close.svg'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useCheckRestrictedWords } from '@/modules/restricted-words/hooks/use-check-restricted-words.ts'

interface Props {
  open: boolean
  onClose: () => void
}

export const CheckWordsDialog = ({ open, onClose }: Props) => {
  const [words, setWords] = useState('')

  const {
    checkRestrictedWords,
    isCheckRestrictedWordsLoading,
    checkResponse,
    resetCheckMutation,
  } = useCheckRestrictedWords()

  const handleCheckClick = useCallback(() => {
    checkRestrictedWords(words)
  }, [checkRestrictedWords, words])

  const handleClose = useCallback(() => {
    resetCheckMutation()
    setWords('')
    onClose()
  }, [resetCheckMutation, onClose])

  return (
    <Dialog
      bigDialog
      show={open}
    >
      <Dialog.Header>
        <Dialog.Title>Check your message for Blacklisted Words</Dialog.Title>
        <Dialog.Description className="text-center">
          Enter any message below to test if it would be blocked by the
          Blacklisted Words check
        </Dialog.Description>
      </Dialog.Header>

      <div className="mt-6">
        <p className="mb-1 text-sm font-medium">Message</p>
        <textarea
          disabled={isCheckRestrictedWordsLoading}
          placeholder="Enter a message"
          value={words}
          onChange={(event) => setWords(event.target.value)}
          rows={5}
          className="w-full rounded-md border bg-card px-3 py-2 text-title focus:border-primary-500 focus:outline-0 focus:outline-primary-500"
        />
        {checkResponse && (
          <div className="mt-5 flex items-center gap-2 rounded-lg border border-danger-default bg-danger-light px-3 py-2 text-danger-default dark:bg-[#280404]">
            <CloseIcon />
            Detected blacklisted words: {checkResponse.restrictedWord}
          </div>
        )}
        {checkResponse === null && (
          <div className="mt-5 flex items-center gap-2 rounded-lg border border-success-default bg-success-light px-3 py-2 text-success-default dark:bg-[#04280C]">
            <CheckIcon />
            No blacklisted words detected
          </div>
        )}
      </div>

      <Dialog.Footer className="gap-2">
        <Button
          disabled={isCheckRestrictedWordsLoading}
          variant="outline"
          onClick={handleClose}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          disabled={!words}
          isLoading={isCheckRestrictedWordsLoading}
          onClick={handleCheckClick}
          className="flex-1"
        >
          Check
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
